import type { DraftKind } from './draft-preview';

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface DraftWixPreview {
  siteId: string;
  draftPostId: string;
}

export interface DraftDelivery {
  url: string;
  scheduled: boolean;
  at: string;
  changesNotLive: boolean;
}

export interface DraftScheduled {
  at: string;
}

export interface DraftState {
  wixPreview: DraftWixPreview | null;
  wixPreviewPending?: boolean;
  delivered: DraftDelivery | null;
  publishAt: string | null;
  scheduled: DraftScheduled | null;
  working: boolean;
  ended: boolean;
  updatedAt?: string;
  superseded?: boolean;
}

export const NO_DRAFT_STATE: DraftState = { wixPreview: null, delivered: null, publishAt: null, scheduled: null, working: false, ended: false };

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function guid(value: unknown): string {
  const id = text(value);
  return GUID.test(id) ? id : '';
}

function httpsUrl(value: unknown): string {
  const raw = text(value);
  try {
    return new URL(raw).protocol === 'https:' ? raw : '';
  } catch {
    return '';
  }
}

function isoInstant(value: unknown): string | null {
  const at = text(value);
  return at && Number.isFinite(Date.parse(at)) ? at : null;
}

function wixPreviewOf(value: unknown): DraftWixPreview | null {
  const preview = record(value);
  const siteId = guid(preview?.siteId);
  const draftPostId = guid(preview?.draftPostId);
  return siteId && draftPostId ? { siteId, draftPostId } : null;
}

const INSTANT = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})(?:\.(\d{1,9}))?(Z|[+-]\d{2}:\d{2})$/i;

function instant(value: string): [number, number] | null {
  const match = INSTANT.exec(value);
  if (!match) return null;
  const ms = Date.parse(`${match[1]}${match[3]}`);
  return Number.isFinite(ms) ? [ms / 1000, Number((match[2] ?? '').padEnd(9, '0'))] : null;
}

function later(a: string, b: string): boolean {
  const first = instant(a);
  const second = instant(b);
  return !!first && !!second && (first[0] > second[0] || (first[0] === second[0] && first[1] > second[1]));
}

function deliveryOf(value: unknown, kind: DraftKind, updatedAt: string): DraftDelivery | null {
  const delivery = record(value);
  if (!delivery) return null;
  const done = text(kind === 'EMAIL' ? delivery.sentAt : delivery.publishedAt);
  const scheduledAt = text(delivery.scheduledAt);
  const at = done || scheduledAt;
  return { url: httpsUrl(delivery.url), scheduled: !done && !!scheduledAt, at, changesNotLive: kind !== 'EMAIL' && later(updatedAt, at) };
}

function scheduledBySavedAction(task: {
  status?: string | null;
  display?: unknown;
  blockedByDecisions?: readonly { decisionId?: string | null; resolvedChoice?: string | null }[] | null;
}): boolean {
  if (task.status !== 'PENDING' || !text(record(task.display)?.savedActionId)) return false;
  return !(task.blockedByDecisions ?? []).some((edge) => !!text(edge?.decisionId) && !text(edge?.resolvedChoice));
}

export function draftPostDone(kind: DraftKind, delivered: DraftDelivery | null | undefined): boolean {
  return kind === 'BLOG_POST' && !!delivered && !delivered.scheduled && !delivered.changesNotLive;
}

type DraftTask = {
  workspaceId?: string | null;
  status?: string | null;
  display?: unknown;
  blockedByDecisions?: readonly { decisionId?: string | null; resolvedChoice?: string | null }[] | null;
};

function socialDraftStateOf(task: DraftTask, display: Record<string, unknown>, draftId: string): DraftState | null {
  const preview = record(display.socialPostPreviewCard);
  if (!preview || text(preview.decisionId)) return null;
  const working = task.status === 'IN_PROGRESS';
  const ended = task.status === 'CANCELLED' || task.status === 'MOOT' || task.status === 'FAILED';
  if (text(preview.draftId).toLowerCase() !== draftId.toLowerCase()) return { ...NO_DRAFT_STATE, working, ended, superseded: true };
  const published = record(display.publishedPost);
  const publishedAt = isoInstant(published?.publishedAt);
  const heldAt = isoInstant(published?.scheduledAt);
  const publishAt = isoInstant(preview.scheduledAt);
  const updatedAt = isoInstant(preview.updatedAt);
  return {
    wixPreview: null,
    delivered: publishedAt ? { url: httpsUrl(published?.url), scheduled: !!heldAt, at: heldAt ?? publishedAt, changesNotLive: false } : null,
    publishAt,
    scheduled: publishAt && scheduledBySavedAction(task) ? { at: publishAt } : null,
    working,
    ended,
    ...(updatedAt ? { updatedAt } : {}),
  };
}

export function draftSuperseded(state: DraftState | undefined, updatedAt: string | undefined): boolean {
  if (state?.superseded) return true;
  const saved = Date.parse(state?.updatedAt ?? '');
  const own = Date.parse(updatedAt ?? '');
  return Number.isFinite(saved) && Number.isFinite(own) && saved !== own;
}

export function draftStateOf(
  task:
    | {
        workspaceId?: string | null;
        status?: string | null;
        display?: unknown;
        blockedByDecisions?: readonly { decisionId?: string | null; resolvedChoice?: string | null }[] | null;
      }
    | null
    | undefined,
  card: { workspaceId: string; draftId: string; kind: DraftKind },
): DraftState | null {
  const display = record(task?.display);
  if (!display || text(task?.workspaceId).toLowerCase() !== card.workspaceId.toLowerCase()) return null;
  if (card.kind === 'SOCIAL_POST') return task ? socialDraftStateOf(task, display, card.draftId) : null;
  const email = card.kind === 'EMAIL';
  const preview = record(display[email ? 'emailPreviewCard' : 'blogPostPreviewCard']);
  if (!preview || text(preview.draftId).toLowerCase() !== card.draftId.toLowerCase()) return null;
  const delivery = record(display.delivery);
  const publishAt = isoInstant(delivery?.publishAt);
  const publishedAt = isoInstant(delivery?.publishedAt);
  const updatedAt = text(preview.updatedAt);
  const wixPreviewPending = !email && (preview.wixPreviewPending === true || !!record(preview.wixPreviewPending));
  const delivered: DraftDelivery | null = publishedAt
    ? { url: httpsUrl(delivery?.url), scheduled: false, at: publishedAt, changesNotLive: !email && later(updatedAt, publishedAt) }
    : deliveryOf(display[email ? 'sentEmail' : 'publishedBlogPost'], card.kind, updatedAt);
  return {
    wixPreview: email || wixPreviewPending ? null : wixPreviewOf(preview.wixPreview),
    ...(wixPreviewPending ? { wixPreviewPending: true } : {}),
    ...(updatedAt ? { updatedAt } : {}),
    delivered,
    publishAt,
    scheduled: publishAt && task && scheduledBySavedAction(task) ? { at: publishAt } : null,
    working: task?.status === 'IN_PROGRESS',
    ended: task?.status === 'CANCELLED' || task?.status === 'MOOT' || task?.status === 'FAILED',
  };
}

export function readDraftState(body: unknown): DraftState {
  const state = record(body);
  const delivered = record(state?.delivered);
  const scheduled = record(state?.scheduled);
  return {
    wixPreview: state?.wixPreviewPending === true ? null : wixPreviewOf(state?.wixPreview),
    ...(state?.wixPreviewPending === true ? { wixPreviewPending: true } : {}),
    delivered: delivered
      ? {
          url: httpsUrl(delivered.url),
          scheduled: delivered.scheduled === true,
          at: text(delivered.at),
          changesNotLive: delivered.changesNotLive === true,
        }
      : null,
    publishAt: isoInstant(state?.publishAt),
    scheduled: scheduled && isoInstant(scheduled.at) ? { at: isoInstant(scheduled.at) as string } : null,
    working: state?.working === true,
    ended: state?.ended === true,
    ...(isoInstant(state?.updatedAt) ? { updatedAt: isoInstant(state?.updatedAt) as string } : {}),
    ...(state?.superseded === true ? { superseded: true } : {}),
  };
}
